/**
 * lib/supabase.js — Cliente Supabase Auth compartilhado.
 *
 * Bootstrap:
 *   1. Chama GET /api/auth/config para pegar URL + publishable key
 *      (evita hardcode e permite trocar chave sem rebuild do frontend).
 *   2. Retorna singleton do @supabase/supabase-js.
 *
 * Session persistence: LocalStorage (default do supabase-js) + autoRefresh.
 * O JWT retornado tem `workspace_id`, `empresa_id`, `is_super_adm`
 * injetados via Custom Access Token Hook (rodando no console Supabase).
 */
import { createClient } from "@supabase/supabase-js";

let _client = null;
let _clientPromise = null;
let _configPromise = null;

async function fetchConfig() {
  if (_configPromise) return _configPromise;
  _configPromise = (async () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const r = await fetch(`${base}/api/auth/config`);
    if (!r.ok) throw new Error(`GET /api/auth/config: ${r.status}`);
    return r.json();
  })();
  return _configPromise;
}

export async function getSupabase() {
  if (_client) return _client;
  // Phase 12.55.30 — Singleton concurrency-safe. Sem esse guard, chamadas
  // simultâneas a getSupabase() (várias telas montando ao mesmo tempo)
  // criavam múltiplas instâncias GoTrueClient com a MESMA storageKey.
  // Isso disparava o warning "Multiple GoTrueClient instances detected"
  // no Supabase e causava tokens perdidos entre instâncias — telas
  // recebiam null em getAccessToken() e exibiam "sem-auth" mesmo com
  // sessão válida no localStorage.
  if (_clientPromise) return _clientPromise;
  _clientPromise = (async () => {
    const cfg = await fetchConfig();
    if (!cfg.supabase_url || !cfg.publishable_key) {
      _clientPromise = null; // permite tentar de novo
      throw new Error("Config Supabase incompleta — verifique backend/.env");
    }
    _client = createClient(cfg.supabase_url, cfg.publishable_key, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storageKey: "sb-valora-auth",
      },
    });
    return _client;
  })();
  return _clientPromise;
}

/**
 * getAccessToken — retorna o JWT válido atual.
 *
 * Phase 12.28 — Fallback com refresh explícito. `autoRefreshToken:true` cobre
 * o caso comum, mas em janelas long-idle (aba deixada aberta, tab dormente)
 * `getSession()` às vezes devolve `null` mesmo com refresh_token ainda válido
 * no storage. Aqui, se o token vier vazio, tentamos `refreshSession()` uma
 * vez antes de desistir. Isso elimina o "Sessão expirada" espúrio em cliques
 * que caem exatamente nesse gap.
 */
export async function getAccessToken() {
  const sb = await getSupabase();
  const { data } = await sb.auth.getSession();
  const tok = data.session?.access_token;
  if (tok) return tok;
  // Fallback: força refresh a partir do refresh_token do storage.
  try {
    const { data: refreshed } = await sb.auth.refreshSession();
    return refreshed?.session?.access_token || null;
  } catch {
    return null;
  }
}
